import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const telHref = (num) => {
  const digits = String(num).replace(/[^\d+]/g, '');
  return digits ? `tel:${digits}` : null;
};

const mailtoHref = (email) => {
  if (!email) return null;
  return `mailto:${email}`;
};

const Field = ({ label, children, className = '' }) => (
  <div className={className}>
    <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
    <dd className="text-sm text-foreground mt-0.5">{children}</dd>
  </div>
);

const contactChip =
  'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border border-border bg-muted/60 text-foreground hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

const FactoryDetail = ({
  factory,
  managers,
  loading,
  managersLoading,
  error,
  onRetry,
  onBack,
}) => {
  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-2/3 rounded bg-muted animate-pulse" />
        <div className="h-40 rounded-lg bg-muted animate-pulse" />
        <div className="h-40 rounded-lg bg-muted animate-pulse" />
      </div>
    );
  }

  if (error) {
    const is404 = /not found|404/i.test(error);
    return (
      <div className="rounded-lg border border-error/30 bg-error/5 p-6 text-center">
        <p className="text-sm font-medium text-error mb-3">
          {is404 ? 'Factory not found.' : error}
        </p>
        <div className="flex justify-center gap-2">
          {onBack && (
            <Button variant="outline" size="sm" onClick={onBack}>
              Back
            </Button>
          )}
          {onRetry && !is404 && (
            <Button variant="default" size="sm" onClick={onRetry} iconName="RefreshCw" iconPosition="left">
              Retry
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (!factory) {
    return (
      <div className="rounded-lg border border-border bg-card p-10 text-center shadow-sm">
        <p className="text-sm text-muted-foreground">Factory not found.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Identity + location + ops */}
      <section className="rounded-lg border border-border bg-card p-4 sm:p-6 shadow-sm">
        <h2 className="text-lg sm:text-xl font-bold text-foreground">
          {factory.factoryName}
        </h2>
        {factory.plantName && (
          <p className="text-sm mt-1 text-muted-foreground">
            {factory.plantName}
          </p>
        )}

        <div className="flex flex-wrap gap-1.5 mt-3">
          {factory.operationalStatus && (
            <span className="text-[11px] px-2 py-0.5 rounded-full font-medium bg-success/10 text-success">
              {factory.operationalStatus}
            </span>
          )}
          {factory.factoryNature && (
            <span className="text-[11px] px-2 py-0.5 rounded-full font-medium bg-primary/15 text-foreground">
              {factory.factoryNature}
            </span>
          )}
          {factory.millCode && (
            <span
              className="text-[11px] px-2 py-0.5 rounded-full font-medium"
              style={{ background: 'rgba(30,74,114,0.1)', color: '#1e4a72' }}
            >
              Mill {factory.millCode}
            </span>
          )}
        </div>

        <dl className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {factory.state && <Field label="State">{factory.state}</Field>}
          {factory.district && <Field label="District">{factory.district}</Field>}
          {factory.city && <Field label="City">{factory.city}</Field>}
          {factory.address && (
            <Field label="Address" className="sm:col-span-2">{factory.address}</Field>
          )}
          {factory.registeredOffice && (
            <Field label="Registered office" className="sm:col-span-2">
              {factory.registeredOffice}
            </Field>
          )}
          {factory.trustedPincode && (
            <Field label="Verified Pincode">
              <span className="font-medium">{factory.trustedPincode}</span>
            </Field>
          )}
          {!factory.trustedPincode && factory.sourcePincode && (
            <Field label="Source Pincode">{factory.sourcePincode}</Field>
          )}
          {factory.trustedPincode &&
            factory.sourcePincode &&
            factory.sourcePincode !== factory.trustedPincode && (
              <Field label="Source Pincode">{factory.sourcePincode}</Field>
            )}
        </dl>

        <div className="flex flex-wrap gap-2 mt-4">
          {(factory.phones || []).map((ph) => {
            const href = telHref(ph);
            if (!href) return null;
            return (
              <a key={`ph-${ph}`} href={href} className={contactChip}>
                <Icon name="Phone" size={14} style={{ color: '#1e4a72' }} />
                {ph}
              </a>
            );
          })}
          {(factory.emails || []).map((em) => {
            const href = mailtoHref(em);
            if (!href) return null;
            return (
              <a key={`em-${em}`} href={href} className={contactChip}>
                <Icon name="Mail" size={14} className="text-success" />
                {em}
              </a>
            );
          })}
          {factory.website && (
            <a
              href={factory.website}
              target="_blank"
              rel="noopener noreferrer"
              className={contactChip}
            >
              <Icon name="Globe" size={14} style={{ color: '#ff8c00' }} />
              Website
            </a>
          )}
        </div>
      </section>

      {/* Managers */}
      <section className="rounded-lg border border-border bg-card p-4 sm:p-6 shadow-sm">
        <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
          <Icon name="Users" size={18} style={{ color: '#ff8c00' }} />
          Managers / Contacts
        </h3>

        {managersLoading && (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-16 rounded-lg bg-muted animate-pulse" />
            ))}
          </div>
        )}

        {!managersLoading && (!managers || managers.length === 0) && (
          <p className="text-sm text-muted-foreground">No contacts listed</p>
        )}

        {!managersLoading && managers?.length > 0 && (
          <ul className="space-y-2">
            {managers.map((m) => {
              const dial = m.mobile || m.phone;
              const dialHref = dial ? telHref(dial) : null;
              const mail = m.email ? mailtoHref(m.email) : null;
              return (
                <li
                  key={m.id}
                  className="rounded-lg border border-border bg-muted/40 p-3"
                >
                  <p className="font-medium text-sm text-foreground">
                    {m.managerName}
                  </p>
                  {m.designation && (
                    <p className="text-xs mt-0.5 text-muted-foreground">
                      {m.designation}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-3 mt-2">
                    {m.mobile && dialHref && (
                      <a
                        href={dialHref}
                        className="inline-flex items-center gap-1 text-xs font-medium hover:underline"
                        style={{ color: '#1e4a72' }}
                      >
                        <Icon name="Smartphone" size={13} />
                        {m.mobile}
                      </a>
                    )}
                    {m.phone && m.phone !== m.mobile && (
                      <a
                        href={telHref(m.phone)}
                        className="inline-flex items-center gap-1 text-xs font-medium hover:underline"
                        style={{ color: '#1e4a72' }}
                      >
                        <Icon name="Phone" size={13} />
                        {m.phone}
                      </a>
                    )}
                    {mail && (
                      <a
                        href={mail}
                        className="inline-flex items-center gap-1 text-xs font-medium text-success hover:underline"
                      >
                        <Icon name="Mail" size={13} />
                        {m.email}
                      </a>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
};

export default FactoryDetail;
